import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { relatorApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Chalkboard, CalendarBlank, Users, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const statusConfig = {
  sin_conformar: { label: 'Sin Conformar', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  conformada: { label: 'Conformada', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  realizada: { label: 'Realizada', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
};

export default function RelatorDashboard() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await relatorApi.getTrainings();
        setTrainings(res.data);
      } catch { toast.error('Error al cargar capacitaciones'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
    </div>
  );

  const active = trainings.filter(t => t.status !== 'realizada');
  const closed = trainings.filter(t => t.status === 'realizada');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="relator-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Dashboard Relator</h1>
        <p className="text-[#2B4B8C]/60 text-sm">Gestiona tus capacitaciones asignadas</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#2BBAA4]/10 flex items-center justify-center">
              <Chalkboard size={24} className="text-[#2BBAA4]" />
            </div>
            <div>
              <p className="text-xs font-medium text-[#2B4B8C]/50 uppercase">Total</p>
              <p className="text-3xl font-bold text-[#2B4B8C]">{trainings.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <CalendarBlank size={24} className="text-blue-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-[#2B4B8C]/50 uppercase">Activas</p>
              <p className="text-3xl font-bold text-[#2B4B8C]">{active.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Users size={24} className="text-green-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-[#2B4B8C]/50 uppercase">Cerradas</p>
              <p className="text-3xl font-bold text-[#2B4B8C]">{closed.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {active.length > 0 && (
        <div>
          <h2 className="text-base font-bold text-[#2B4B8C] mb-3">Capacitaciones Activas</h2>
          <div className="grid gap-3">
            {active.map(t => (
              <Card key={t.id} className="bg-white border-[#2B4B8C]/10 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/relator/trainings/${t.id}`)} data-testid={`training-card-${t.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#2B4B8C]/5 flex items-center justify-center shrink-0">
                      <Chalkboard size={20} className="text-[#2B4B8C]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[#2B4B8C] truncate">{t.name}</p>
                      <div className="flex items-center gap-2 text-xs text-[#2B4B8C]/50 mt-0.5">
                        {t.course_name && <span>{t.course_name}</span>}
                        {t.client_name && <><span>-</span><span>{t.client_name}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right text-xs text-[#2B4B8C]/50">
                      <p>{t.scheduled_date ? new Date(t.scheduled_date).toLocaleDateString('es-CL') : '-'}</p>
                      <p>{t.enrolled_count}/{t.max_participants} inscritos</p>
                    </div>
                    <Badge variant="outline" className={statusConfig[t.status]?.className || 'bg-gray-100 text-gray-600'}>{statusConfig[t.status]?.label || t.status}</Badge>
                    <ArrowRight size={16} className="text-[#2B4B8C]/30" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {closed.length > 0 && (
        <div>
          <h2 className="text-base font-bold text-[#2B4B8C] mb-3">Capacitaciones Cerradas</h2>
          <div className="grid gap-3">
            {closed.map(t => (
              <Card key={t.id} className="bg-white border-[#2B4B8C]/10 opacity-70 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => navigate(`/relator/trainings/${t.id}`)} data-testid={`training-closed-${t.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-green-500/5 flex items-center justify-center shrink-0">
                      <Chalkboard size={20} className="text-green-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[#2B4B8C] truncate">{t.name}</p>
                      <p className="text-xs text-[#2B4B8C]/50">{t.course_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Realizada</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {trainings.length === 0 && (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="py-16 text-center text-[#2B4B8C]/40">
            <Chalkboard size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No tienes capacitaciones asignadas</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
